/**
 * TemplateSectionsList Component
 *
 * List container for template sections with drag-and-drop reordering using @dnd-kit.
 * V2 system: sections have inline content (no module references).
 */

import React, { useState } from 'react';
import { BetweenItemAddButton } from '@components/ui/buttons/BetweenItemAddButton';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Paper, Typography } from '@mui/material';

import type { SectionWithMeta } from '../types';

import { AddSectionCard } from './AddSectionCard';
import { TemplateSectionItem } from './TemplateSectionItem';

interface TemplateSectionsListProps {
  /** List of sections with metadata */
  sections: SectionWithMeta[];
  /** Whether sections can be edited */
  canEdit: boolean;
  /** Callback when sections are reordered */
  onReorderSections: (startIndex: number, endIndex: number) => void;
  /** Callback to remove a section */
  onRemoveSection: (sectionId: string) => void;
  /** Callback to add section */
  onAddSection: (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => void;
  /** Callback to update section content */
  onUpdateSectionContent: (sectionId: string, content: string) => Promise<boolean>;
  /** Callback to update section title */
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  /** Callback to open section library modal with insertion index */
  onBrowseLibrary?: (index: number) => void;
}

export const TemplateSectionsList: React.FC<TemplateSectionsListProps> = ({
  sections,
  canEdit,
  onReorderSections,
  onRemoveSection,
  onAddSection,
  onBrowseLibrary,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure drag sensors - require 8px drag before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderSections(oldIndex, newIndex);
    }
  };

  const activeSection = activeId ? sections.find(s => s.id === activeId) : null;

  // Empty state - show single "Add Section" card
  if (sections.length === 0) {
    return (
      <Box sx={{ py: 4 }}>
        {canEdit ? (
          <AddSectionCard onClick={() => onBrowseLibrary?.(0)} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              This template has no sections.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Render with drag-and-drop if editable
  if (canEdit) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.BeforeDragging,
          },
        }}
      >
        <SortableContext
          items={sections.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            {/* Add section button at the top */}
            <BetweenItemAddButton
              enabled={activeId === null}
              onClick={() => onBrowseLibrary?.(0)}
              label="Section"
            />

            {sections.map((section, index) => (
              <React.Fragment key={section.id}>
                <TemplateSectionItem
                  section={section}
                  canEdit={canEdit}
                  onRemove={() => onRemoveSection(section.id)}
                />
                {/* Add section button after each item */}
                <BetweenItemAddButton
                  enabled={activeId === null}
                  onClick={() => onBrowseLibrary?.(index + 1)}
                  label="Section"
                />
              </React.Fragment>
            ))}
          </Box>
        </SortableContext>

        {/* DragOverlay for smooth dragging experience */}
        <DragOverlay
          dropAnimation={
            activeSection
              ? null // No animation on successful drop to prevent flash
              : {
                  duration: 500,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }
          }
        >
          {activeSection && (
            <Paper
              elevation={8}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: 'grabbing',
                opacity: 0.9,
                border: 2,
                borderColor: 'primary.main',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {activeSection.title}
              </Typography>
            </Paper>
          )}
        </DragOverlay>
      </DndContext>
    );
  }

  // Read-only view (no drag-and-drop)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {sections.map(section => (
        <TemplateSectionItem
          key={section.id}
          section={section}
          canEdit={false}
        />
      ))}
    </Box>
  );
};
